import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import EastIcon from '@mui/icons-material/East';

const steps = [
  {
    number: '01',
    icon: <AutoAwesomeIcon sx={{ fontSize: 32 }} />,
    title: 'Create Your Study Loop',
    description:
      'Build a loop manually by adding your own cards, or let our AI generate a complete set of questions from any topic, notes, or document in seconds.',
    detail: 'Flashcards · Multiple choice · AI-generated or handcrafted',
  },
  {
    number: '02',
    icon: <PlayCircleOutlineIcon sx={{ fontSize: 32 }} />,
    title: 'Study & Get Tested',
    description:
      'Cycle through your cards at your own pace. Answer questions, flip cards, and mark how well you know each one. BrainLoop tracks every response.',
    detail: 'Timed sessions · Self-paced · Progress tracked in real time',
  },
  {
    number: '03',
    icon: <ReplayIcon sx={{ fontSize: 32 }} />,
    title: 'Reinforce What You Missed',
    description:
      'After every session, see a full breakdown of your results. Choose to restudy only the cards you got wrong — or run the full loop again. Repeat until you own it.',
    detail: 'Targeted review · Session summaries · Loop until mastered',
  },
];

export function HowItWorksSection() {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.72rem',
              display: 'block',
              mb: 2,
            }}
          >
            The Loop Method
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            sx={{ fontWeight: 800, color: 'common.white', mb: 2 }}
          >
            How BrainLoop Works
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: 560,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Three simple steps that compound into real, lasting mastery.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: { xs: 4, md: 0 },
          }}
        >
          {steps.map((step, index) => (
            <Box
              key={step.number}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'stretch',
                flex: 1,
              }}
            >
              {/* Clay card */}
              <motion.div
                style={{ flex: 1, display: 'flex' }}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: index * 0.15 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    p: { xs: 3.5, md: 4 },
                    borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.11)',
                    border: '1px solid rgba(255,255,255,0.16)',
                    boxShadow: [
                      '0 16px 40px rgba(0,0,0,0.4)',
                      '0 6px 12px rgba(0,0,0,0.25)',
                      'inset 0 2px 0 rgba(255,255,255,0.2)',
                      'inset 0 -2px 0 rgba(0,0,0,0.18)',
                    ].join(', '),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-6px) scale(1.01)',
                      boxShadow: [
                        '0 24px 52px rgba(0,0,0,0.48)',
                        '0 8px 16px rgba(0,0,0,0.3)',
                        'inset 0 2px 0 rgba(255,255,255,0.22)',
                        'inset 0 -2px 0 rgba(0,0,0,0.2)',
                      ].join(', '),
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: '0.75rem',
                      letterSpacing: 2,
                      color: 'rgba(255,255,255,0.35)',
                    }}
                  >
                    STEP {step.number}
                  </Typography>

                  {/* Clay icon orb */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
                      boxShadow: [
                        '0 10px 24px rgba(99,102,241,0.5)',
                        '0 4px 8px rgba(0,0,0,0.22)',
                        'inset 0 2px 0 rgba(255,255,255,0.3)',
                        'inset 0 -2px 0 rgba(0,0,0,0.18)',
                      ].join(', '),
                      color: 'white',
                      flexShrink: 0,
                    }}
                  >
                    {step.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{ fontWeight: 700, color: 'white', lineHeight: 1.3 }}
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>

                  <Box
                    sx={{
                      mt: 'auto',
                      pt: 1,
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.45)', display: 'block' }}
                    >
                      {step.detail}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>

              {/* Arrow connector */}
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <EastIcon sx={{ color: 'rgba(255,255,255,0.22)', fontSize: 26 }} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
